import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type LandingPromotionData = {
  enabled: boolean;
  eyebrow: string;
  headlinePrimary: string;
  headlineSecondary: string;
  promoCodePrefix: string;
  promoCode: string;
  buttonLabel: string;
  buttonLink: string;
  imageUrl: string;
  gradientFrom: string;
  gradientVia: string;
  gradientTo: string;
  accentColor: string;
  textColor: string;
  countdownEndsAt?: string;
  countdownLabel?: string;
  /** 50–1000: % of layout max size for hero product image */
  imageScalePercent?: number;
};

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function fullImageUrl(raw: string): string {
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/uploads/')) return `${API_BASE}${raw}`;
  return raw;
}

type Props = {
  data: LandingPromotionData;
};

function useWindowDimensions() {
  const [dims, setDims] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1200,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  useEffect(() => {
    const onResize = () =>
      setDims({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return dims;
}

/**
 * Pixel caps for hero product (same logic as previous CSS, but reliable in all browsers).
 * `scale` is admin percent / 100 (e.g. 1000% → 10).
 */
function computePromoImageMaxPx(
  scale: number,
  vw: number,
  vh: number,
): { maxH: number; maxW: number } {
  const s = Math.min(10, Math.max(0.5, scale));
  const vhU = vh / 100;
  const vwU = vw / 100;

  let maxH: number;
  let maxW: number;

  if (vw >= 1536) {
    maxH = Math.min(vh - 118, 1440) * s;
    maxW = Math.min(vw * 0.68, 1520) * s;
  } else if (vw >= 1280) {
    maxH = Math.min(vh - 118, 1440) * s;
    maxW = Math.min(vw * 0.7, 1440) * s;
  } else if (vw >= 1024) {
    maxH = Math.min(vh - 124, 1320) * s;
    maxW = Math.min(vw * 0.72, 1320) * s;
  } else if (vw >= 640) {
    maxH = Math.min(62 * vhU, 640) * s;
    maxW = Math.min(96 * vwU, 820) * s;
  } else {
    maxH = Math.min(58 * vhU, 560) * s;
    maxW = Math.min(98 * vwU, 720) * s;
  }

  return {
    maxH: Math.max(96, Math.round(maxH)),
    maxW: Math.max(96, Math.round(maxW)),
  };
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

/** Reference-style floating hearts — same hue family, depth only */
function PromoFloatingHearts({ color }: { color: string }) {
  const items = [
    { top: '10%', left: '6%', size: 26, opacity: 0.16, rot: -8, delay: '0s' },
    { top: '18%', left: '38%', size: 14, opacity: 0.1, rot: 12, delay: '1.2s' },
    { top: '8%', left: '72%', size: 20, opacity: 0.12, rot: -4, delay: '0.4s' },
    { top: '52%', left: '4%', size: 18, opacity: 0.11, rot: 6, delay: '2s' },
    { top: '62%', left: '48%', size: 12, opacity: 0.08, rot: -14, delay: '0.8s' },
    { top: '42%', left: '88%', size: 22, opacity: 0.14, rot: 10, delay: '1.6s' },
    { top: '78%', left: '22%', size: 16, opacity: 0.09, rot: -6, delay: '2.4s' },
    { top: '84%', left: '58%', size: 24, opacity: 0.13, rot: 4, delay: '1s' },
    { top: '28%', left: '92%', size: 15, opacity: 0.1, rot: -10, delay: '3s' },
  ];

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      {items.map((h, i) => (
        <span
          key={i}
          className="promo-heart-float absolute text-[length:var(--hs)]"
          style={
            {
              top: h.top,
              left: h.left,
              color,
              opacity: h.opacity,
              ['--hs' as string]: `${h.size}px`,
              ['--rot' as string]: `${h.rot}deg`,
              animationDelay: h.delay,
            } as React.CSSProperties
          }
        >
          <HeartIcon className="h-[1em] w-[1em]" />
        </span>
      ))}
    </div>
  );
}

function PromoCornerRibbon({
  label,
  fill,
  textColor,
}: {
  label: string;
  fill: string;
  textColor: string;
}) {
  const short =
    label.length > 14 ? `${label.slice(0, 11)}…` : label || 'SALE';
  return (
    <div
      className="pointer-events-none absolute right-4 top-4 z-20 sm:right-8 sm:top-6 lg:right-10 lg:top-8"
      aria-hidden
    >
      <div className="relative flex h-[4.5rem] w-[4.5rem] items-center justify-center sm:h-[5.25rem] sm:w-[5.25rem]">
        <span
          className="absolute inset-0 block drop-shadow-lg"
          style={{ color: fill }}
        >
          <HeartIcon className="h-full w-full" />
        </span>
        <span
          className="relative z-[1] max-w-[3.2rem] text-center text-[9px] font-black uppercase leading-tight tracking-tight sm:max-w-[3.5rem] sm:text-[10px]"
          style={{
            color: textColor,
            textShadow: '0 1px 0 rgba(255,255,255,0.85)',
          }}
        >
          {short}
        </span>
      </div>
    </div>
  );
}

function useCountdown(endIso: string | undefined) {
  const [, setTick] = useState(0);
  const end = useMemo(() => {
    const raw = endIso?.trim();
    if (!raw) return NaN;
    const t = Date.parse(raw);
    return Number.isFinite(t) ? t : NaN;
  }, [endIso]);

  const active = Number.isFinite(end) && end > Date.now();

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  if (!active) return null;

  const ms = Math.max(0, end - Date.now());
  const sec = Math.floor(ms / 1000);
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;

  return { days, hours, minutes, seconds };
}

function PromotionCountdown({
  endIso,
  label,
  textColor,
}: {
  endIso: string | undefined;
  label: string;
  textColor: string;
}) {
  const parts = useCountdown(endIso);
  if (!parts) return null;

  const subtle = hexToRgba(textColor, 0.88);
  const segments = [
    { value: String(parts.days).padStart(2, '0'), key: 'd', cap: 'Days' },
    { value: String(parts.hours).padStart(2, '0'), key: 'h', cap: 'Hrs' },
    { value: String(parts.minutes).padStart(2, '0'), key: 'm', cap: 'Min' },
    { value: String(parts.seconds).padStart(2, '0'), key: 's', cap: 'Sec' },
  ];

  return (
    <div
      className="promo-reveal mt-8 max-w-lg lg:mt-10 lg:max-w-xl"
      style={{ animationDelay: '0.35s' }}
    >
      {label ? (
        <p
          className="mb-3 text-xs font-bold uppercase tracking-[0.25em] sm:text-sm lg:text-base"
          style={{ color: subtle }}
        >
          {label}
        </p>
      ) : null}
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {segments.map((s) => (
          <div
            key={s.key}
            className="rounded-lg border px-2 py-3 text-center sm:py-3.5 lg:py-4"
            style={{
              borderColor: hexToRgba(textColor, 0.35),
              backgroundColor: hexToRgba(textColor, 0.1),
            }}
          >
            <div
              className="text-2xl font-black tabular-nums leading-none sm:text-3xl lg:text-4xl"
              style={{ color: textColor }}
            >
              {s.value}
            </div>
            <div
              className="mt-1.5 text-[9px] font-bold uppercase tracking-wider opacity-75 sm:text-[10px] lg:text-xs"
              style={{ color: subtle }}
            >
              {s.cap}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Storefront promo: one continuous brand canvas (reference layout) —
 * copy left, product cutout right on the same background, floating hearts, corner ribbon.
 */
export function LandingPromoShowcase({ data }: Props) {
  const navigate = useNavigate();
  const img = fullImageUrl(data.imageUrl);

  const from = data.gradientFrom;
  const via = data.gradientVia;
  const to = data.gradientTo;

  const panelVars = {
    '--promo-from': from,
    '--promo-via': via,
    '--promo-to': to,
    '--promo-accent': data.accentColor,
    '--promo-text': data.textColor,
  } as React.CSSProperties;

  /** Layered reds so the right side stays rich (not a flat muddy slab); admin colors still drive the palette. */
  const canvasBg: React.CSSProperties = {
    background: `
      radial-gradient(ellipse 85% 115% at 92% 45%, ${hexToRgba(via, 0.45)} 0%, transparent 55%),
      radial-gradient(ellipse 65% 90% at 8% 55%, ${hexToRgba(from, 0.35)} 0%, transparent 50%),
      linear-gradient(106deg, ${from} 0%, ${via} 42%, ${to} 100%)
    `,
    color: data.textColor,
  };

  const heartTone = hexToRgba(via, 0.9);

  const imageScalePercent = Math.min(
    1000,
    Math.max(50, Math.round(Number(data.imageScalePercent) || 100)),
  );
  const imageScaleUnit = imageScalePercent / 100;

  const { w: winW, h: winH } = useWindowDimensions();
  const promoImgMax = useMemo(
    () => computePromoImageMaxPx(imageScaleUnit, winW, winH),
    [imageScaleUnit, winW, winH],
  );

  const onCta = () => {
    const target = data.buttonLink?.trim() || '#products';
    if (target.startsWith('#')) {
      const el = document.querySelector(target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(target);
    }
  };

  return (
    <section
      id="home"
      className="storefront-hero-flush relative mb-0 flex min-h-screen flex-col overflow-hidden rounded-none shadow-[0_12px_40px_-14px_rgba(0,0,0,0.28)] ring-1 ring-black/[0.04]"
      style={{ ...panelVars, ...canvasBg }}
      aria-label={data.headlinePrimary}
    >
      <PromoFloatingHearts color={heartTone} />

      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.06] via-transparent to-black/[0.12]"
        aria-hidden
      />

      {/* Lift product side so photography doesn’t sit in a dead zone */}
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-full max-w-[58%] opacity-90 lg:opacity-100"
        style={{
          background: `radial-gradient(ellipse 85% 95% at 72% 58%, ${hexToRgba(data.textColor, 0.16)} 0%, transparent 58%)`,
        }}
        aria-hidden
      />

      <PromoCornerRibbon
        label={data.headlinePrimary}
        fill={data.textColor}
        textColor={via}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-[1920px] flex-1 flex-col min-h-0 lg:flex-row lg:items-stretch">
        {/* Copy — left */}
        <div className="flex flex-1 flex-col justify-center px-6 pb-6 pt-3 sm:px-10 sm:pb-8 sm:pt-4 lg:max-w-[44%] lg:pb-8 lg:pl-10 lg:pr-4 lg:pt-4 xl:pl-16 xl:pr-8 2xl:pl-20">
          <div className="relative z-[3] max-w-2xl lg:max-w-[42rem] 2xl:max-w-3xl">
            {data.eyebrow ? (
              <p
                className="promo-reveal mb-4 text-xs font-bold uppercase tracking-[0.35em] text-white/90 sm:text-sm lg:mb-5 lg:text-base"
                style={{ animationDelay: '0.04s' }}
              >
                {data.eyebrow}
              </p>
            ) : null}

            <h1
              className="promo-reveal text-balance text-5xl font-black leading-[0.9] tracking-[-0.03em] drop-shadow-[0_6px_36px_rgba(0,0,0,0.25)] sm:text-6xl md:text-7xl lg:text-8xl lg:leading-[0.88] xl:text-[6.5rem] 2xl:text-[7.5rem]"
              style={{ color: data.textColor, animationDelay: '0.1s' }}
            >
              {data.headlinePrimary}
            </h1>

            {data.headlineSecondary ? (
              <h2
                className="promo-reveal mt-6 text-balance text-lg font-bold uppercase leading-snug tracking-[0.14em] text-white/95 sm:text-xl md:text-2xl lg:mt-8 lg:text-3xl xl:text-4xl"
                style={{ animationDelay: '0.18s' }}
              >
                {data.headlineSecondary}
              </h2>
            ) : null}

            <PromotionCountdown
              endIso={data.countdownEndsAt}
              label={data.countdownLabel?.trim() ?? ''}
              textColor={data.textColor}
            />

            {data.promoCodePrefix || data.promoCode ? (
              <div
                className="promo-reveal mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-base sm:text-lg lg:mt-10 lg:text-xl"
                style={{ animationDelay: '0.32s' }}
              >
                {data.promoCodePrefix ? (
                  <span className="font-semibold text-white/90">
                    {data.promoCodePrefix}
                  </span>
                ) : null}
                {data.promoCode ? (
                  <span className="inline-flex rounded-lg border border-white/45 bg-white/14 px-4 py-2 font-mono text-lg font-bold tracking-wider text-white shadow-md backdrop-blur-sm sm:text-xl lg:px-5 lg:py-2.5 lg:text-2xl">
                    {data.promoCode}
                  </span>
                ) : null}
              </div>
            ) : null}

            <div className="promo-reveal mt-10 lg:mt-12" style={{ animationDelay: '0.42s' }}>
              <button
                type="button"
                onClick={onCta}
                className="promo-cta inline-flex min-h-[3.5rem] items-center justify-center rounded-md px-12 py-3.5 text-base font-bold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5 sm:min-h-[3.75rem] sm:px-14 sm:text-lg lg:min-h-[4rem] lg:px-16 lg:text-xl"
                style={{
                  backgroundColor: data.accentColor,
                  boxShadow: `0 14px 40px ${hexToRgba(data.accentColor, 0.48)}, 0 0 0 1px ${hexToRgba(data.textColor, 0.12)} inset`,
                }}
              >
                {data.buttonLabel}
              </button>
            </div>
          </div>
        </div>

        {/* Product — fills remaining height beside copy */}
        <div className="relative flex min-h-[min(280px,42vh)] flex-1 flex-col items-center justify-center px-3 pb-5 pt-2 sm:px-6 sm:pb-7 lg:min-h-0 lg:max-w-[56%] lg:items-center lg:justify-center lg:px-4 lg:pb-6 lg:pt-4 xl:pr-10 2xl:pr-16">
          {img ? (
            <img
              src={img}
              alt=""
              decoding="async"
              fetchPriority="high"
              className="relative z-[1] h-auto w-auto object-contain object-center drop-shadow-[0_32px_80px_rgba(0,0,0,0.4)]"
              style={{
                maxHeight: promoImgMax.maxH,
                maxWidth: promoImgMax.maxW,
              }}
            />
          ) : (
            <div
              className="flex h-40 w-40 items-center justify-center rounded-lg border border-dashed border-white/25 text-sm text-white/40 lg:h-56 lg:w-56"
              aria-hidden
            >
              Image
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default LandingPromoShowcase;
